import { Component, OnInit, OnDestroy, Input, ChangeDetectorRef, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, of, forkJoin } from 'rxjs';
import { map, switchMap, catchError, take } from 'rxjs/operators';
import { SubMenuService } from '../../services/submenu.service';
import { WaypointService } from '../../services/waypoint.service';
import { TripService } from '../../services/trip.service';
import { RideAlongService } from '../../services/ride-along.service';
import { VesselService } from '../../services/vessel.service';
import { AuthService } from '../../core/auth/auth.service';
import { UserProfileService } from '../../services/user-profile.service';
import { SocialService } from '../../services/social.service';
import { ClarityService } from '../../services/clarity.service';
import { ToastService } from '../../services/toast.service';
import { TourAnchorDirective } from '../../features/learning-center/engine/tour-anchor.directive';
import { TourController } from '../../features/learning-center/engine/tour-controller.service';
import { getWaypointPinAsset, hasWaypointPinArt, resolveWaypointType } from '../../shared/utils/waypoint-icon.util';
import {
  FLEET_RECIPIENT_OFFSET,
  RestrictiveFleet,
  isRestrictiveAsMember,
  isRestrictiveRecipientDisabled,
  normalizeRestrictiveRecipientSelection,
  toggledRestrictiveRecipientSelection,
} from '../../shared/utils/restrictive-sharing.utils';
import maplibregl, { Map as MaplibreMap } from 'maplibre-gl';
import { environment } from '../../../environments/environment';


interface WaypointType {
  id: string;
  name: string;
  selected: boolean;
  icon: string;
  payloadType?: string;
  categoryKey?: string;
}

interface WaypointSubtype extends WaypointType {
  markerIcon?: string;
}

/**
 * Wizard step identifiers for the create/edit waypoint flow. Mirrors the app's
 * `_Step` enum (waypoint_sheet.dart).
 *
 * Flow: [changeLocation (edit only) →] topLevel → per-type chain → photo →
 * details → privacy. Photo sits right after the type chain to encourage
 * uploads; `details` is one combined step (Name+Caption for alerts/places,
 * Caption-only for events — Fish Caught's quantity has its own step). Alerts
 * skip `privacy` (always public).
 */
export type WizardStep =
  | 'changeLocation'
  | 'topLevel'
  | 'eventsSubtype'
  | 'fishFamily'
  | 'fishSpecies'
  | 'fishQuantity'
  | 'placesSubtype'
  | 'alertsSubtype'
  | 'patrolJurisdiction'
  | 'photo'
  | 'details'
  | 'privacy';

/** A single-select row rendered by one of the wizard's list steps. */
interface WizardOption {
  id: string;
  label: string;
  icon: string;
  /** Fallback image swapped in by (error) when [icon] 404s. */
  fallbackIcon?: string;
  /** Spotlight anchor id for the guided tour, when this row carries one. */
  anchor?: string | null;
}

@Component({
  selector: 'app-waypoint',
  standalone: true,
  imports: [CommonModule, FormsModule, TourAnchorDirective],
  templateUrl: './waypoint.component.html',
  styleUrls: ['./waypoint.component.scss'],
})
export class WaypointComponent implements OnInit, OnDestroy {
  private readonly defaultFishTypeId = 'yellowfin';
  private readonly maxMediaItems = 3;
  private readonly fishCaughtTypeId = 'fish_caught';
  /** Family-less "Others" catch — see [eventTypes]. */
  private readonly fishCaughtOtherId = 'fish_caught_other';
  private readonly freshObservationThresholdMinutes = 15;
  isOpen = false;
  dataBridgeEnabled = false;
  hasFreshGNSS = false;
  isCreatingWaypoint = false;
  private userGpsPosition: { lat: number; lng: number } | null = null;
  // Privacy selection state
  description = '';
  visibility: 'private' | 'custom' | 'public' = 'private';
  conditions: any = null;
  conditionsLoading = false;
  isResolvingCoordinates = false;
  currentDate = new Date();
  fishQuantity = 1;
  placeName = '';
  showIcon = true;

  visibilitySearchQuery = '';
  selectedMedia: { file: File, url: string }[] = [];
  @Input() mapCenter: { lat: number; lng: number } | null = null;
  coords: { lat: number; lng: number } | null = null;
  originalCoords: { lat: number; lng: number } | null = null;
  private requestedCoords: { lat: number; lng: number } | null = null;
  baselineDataBridgeEnabled = false;
  showCoordPicker = false;
  private coordPickerMap: MaplibreMap | null = null;
  pickedCoords: { lat: number; lng: number } | null = null;
  locatingMe = false;
  pickedCoordsDisplay = '';

  // ── Wizard navigation state ───────────────────────────────────────────────
  // `currentStep` is the step on screen; `history` is the stack of previously
  // visited steps, popped on Back.
  currentStep: WizardStep = 'topLevel';
  history: WizardStep[] = [];
  /** Top-level pick: '' | 'alerts' | 'places' | 'events'. */
  topLevelCategory = '';
  /** Events sub-pick: '' | 'fish_caught' | 'fish_mark' | 'bait_mark' | 'temp_break'. */
  eventsSubtype = '';
  /** Fish Caught family: '' | 'tuna' | 'billfish' | 'pelagic' | 'inshore' | 'other'. */
  fishFamily = '';
  /** Patrol jurisdiction (Alerts → Patrol only). Emitted as details.patrol_jurisdiction. */
  patrolJurisdiction: string | null = null;

  // ── Edit mode ─────────────────────────────────────────────────────────────
  /** Non-null puts the wizard in EDIT mode (seeded, change-location first). */
  editWaypoint: any | null = null;
  /** Already-uploaded photos shown on the Photo step; removals queue below. */
  existingImageUrls: string[] = [];
  private removedImageUrls: string[] = [];
  isDeleting = false;
  showDeleteConfirm = false;
  /**
   * Edit + custom privacy: false while the waypoint's existing shared-with
   * audience is still loading. Gates the always-on edit ✓ so a fast tap can't
   * submit before it arrives and re-share with an empty (not-yet-fetched)
   * selection. Only the initial custom-edit load flips it false.
   */
  private editSharedWithLoaded = true;

  // Real data for Pier Circle
  fleets: any[] = [];
  dockmates: any[] = [];


  eventTypes: WaypointType[] = [
    {
      id: 'tuna',
      name: 'Tuna Caught',
      selected: true,
      icon: '/assets/home/wavepoint/fishcaugh_logo.svg',
      payloadType: this.fishCaughtTypeId,
      categoryKey: 'tuna',
    },
    {
      id: 'billfish',
      name: 'Billfish Caught',
      selected: false,
      icon: '/assets/home/wavepoint/fishcaugh_logo.svg',
      payloadType: this.fishCaughtTypeId,
      categoryKey: 'billfish',
    },
    {
      id: 'pelagic',
      name: 'Pelagic Caught',
      selected: false,
      icon: '/assets/home/wavepoint/fishcaugh_logo.svg',
      payloadType: this.fishCaughtTypeId,
      categoryKey: 'pelagic',
    },
    {
      id: 'inshore',
      name: 'Inshore Caught',
      selected: false,
      icon: '/assets/home/wavepoint/fishcaugh_logo.svg',
      payloadType: this.fishCaughtTypeId,
      categoryKey: 'inshore',
    },
    {
      // "Others" on the family step: a fish_caught with no named family and no
      // species (details carry `fish_subtype: 'other'`). Deliberately has no
      // categoryKey — that absence is what marks it family-less.
      id: this.fishCaughtOtherId,
      name: 'Fish Caught',
      selected: false,
      icon: '/assets/home/wavepoint/fishcaugh_logo.svg',
      payloadType: this.fishCaughtTypeId,
    },
    {
      id: 'fish_mark',
      name: 'Fish Mark',
      selected: false,
      icon: '/assets/home/filter/fishmark_logo.svg',
    },
    {
      id: 'bait_mark',
      name: 'Bait Mark',
      selected: false,
      icon: '/assets/home/wavepoint/baitmark_logo.svg',
    },
    {
      id: 'temp_break',
      name: 'Temp Break',
      selected: false,
      icon: '/assets/home/wavepoint/tempbreak_logo.svg',
    },
  ];


  fishTypes: WaypointSubtype[] = [
    { id: 'yellowfin', name: 'Yellowfin', selected: false, icon: 'assets/waypoint_icons/caught_yellowfin_tab.png', markerIcon: '/assets/waypoint_icons/caught_yellowfin_dot.png' },
    { id: 'bluefin', name: 'Bluefin', selected: false, icon: 'assets/waypoint_icons/caught_bluefin_tab.png', markerIcon: 'assets/waypoint_icons/bluefin_icon.svg' },
    { id: 'bigeye', name: 'Bigeye', selected: false, icon: 'assets/waypoint_icons/caught_bigeye_tab.png', markerIcon: 'assets/waypoint_icons/bigeye_icon.svg' },
    { id: 'albacore', name: 'Albacore', selected: false, icon: 'assets/waypoint_icons/caught_albacore_tab.png', markerIcon: 'assets/waypoint_icons/longfin_icon.svg' },
    { id: 'bluemarlin', name: 'Blue Marlin', selected: false, icon: 'assets/waypoint_icons/caught_bluemarlin_tab.png', markerIcon: 'assets/waypoint_icons/bluemarlin_icon.svg' },
    { id: 'whitemarlin', name: 'White Marlin', selected: false, icon: 'assets/waypoint_icons/caught_whitemarlin_tab.png', markerIcon: 'assets/waypoint_icons/whitemarlin_icon.svg' },
    { id: 'sailfish', name: 'Sailfish', selected: false, icon: 'assets/waypoint_icons/caught_sailfish_tab.png', markerIcon: 'assets/waypoint_icons/othertype_icon.svg' },
    { id: 'swordfish', name: 'Swordfish', selected: false, icon: 'assets/waypoint_icons/caught_swordfish_tab.png', markerIcon: 'assets/waypoint_icons/othertype_icon.svg' },
    { id: 'mahi', name: 'Mahi', selected: false, icon: 'assets/waypoint_icons/caught_mahi_tab.png', markerIcon: 'assets/waypoint_icons/othertype_icon.svg' },
    { id: 'wahoo', name: 'Wahoo', selected: false, icon: 'assets/waypoint_icons/caught_wahoo_tab.png', markerIcon: 'assets/waypoint_icons/othertype_icon.svg' },
    { id: 'stripedbass', name: 'Striped Bass', selected: false, icon: 'assets/waypoint_icons/caught_stripedbass_tab.png', markerIcon: 'assets/waypoint_icons/othertype_icon.svg' },
    { id: 'bluefish', name: 'Bluefish', selected: false, icon: 'assets/waypoint_icons/caught_bluefish_tab.png', markerIcon: 'assets/waypoint_icons/othertype_icon.svg' },
    { id: 'tautog', name: 'Tautog', selected: false, icon: 'assets/waypoint_icons/caught_tautog_tab.png', markerIcon: 'assets/waypoint_icons/othertype_icon.svg' },
    { id: 'seabass', name: 'Sea Bass', selected: false, icon: 'assets/waypoint_icons/caught_seabass_tab.png', markerIcon: 'assets/waypoint_icons/othertype_icon.svg' },
    { id: 'fluke', name: 'Fluke', selected: false, icon: 'assets/waypoint_icons/caught_fluke_tab.png', markerIcon: 'assets/waypoint_icons/othertype_icon.svg' },
    { id: 'general_tuna', name: 'Other', selected: false, icon: 'assets/waypoint_icons/caught_general_tuna_tab.png', markerIcon: 'assets/waypoint_icons/othertype_icon.svg' },
    { id: 'general_billfish', name: 'Other', selected: false, icon: 'assets/waypoint_icons/caught_general_billfish_tab.png', markerIcon: 'assets/waypoint_icons/othertype_icon.svg' },
    { id: 'general_pelagic', name: 'Other', selected: false, icon: 'assets/waypoint_icons/caught_general_pelagic_tab.png', markerIcon: 'assets/waypoint_icons/othertype_icon.svg' },
    { id: 'general_inshore', name: 'Other', selected: false, icon: 'assets/waypoint_icons/caught_general_inshore_tab.png', markerIcon: 'assets/waypoint_icons/othertype_icon.svg' },
  ];

  placeTypes: WaypointType[] = [
    {
      id: 'canyon',
      name: 'Spot',
      selected: false,
      icon: 'assets/waypoint_icons/spot_create.png',
    },
    {
      id: 'wreck',
      name: 'Wreck',
      selected: false,
      icon: 'assets/waypoint_icons/wreck_create.png',
    },
    {
      id: 'inlet',
      name: 'Inlet',
      selected: false,
      icon: 'assets/waypoint_icons/inlet_create.png',
    },
    {
      id: 'anchorage',
      name: 'Anchorage',
      selected: false,
      icon: 'assets/waypoint_icons/anchorage_create.png',
    },
    {
      id: 'home',
      name: 'Home',
      selected: false,
      icon: 'assets/waypoint_icons/home_create.png',
    },
  ];

  alertTypes: WaypointType[] = [
    {
      id: 'hazard',
      name: 'Hazard',
      selected: false,
      icon: 'assets/waypoint_icons/hazard_create.png',
    },
    {
      id: 'diver',
      name: 'Diver',
      selected: false,
      icon: 'assets/waypoint_icons/diver_create.png',
    },
    {
      id: 'patrol',
      name: 'Patrol',
      selected: false,
      icon: 'assets/waypoint_icons/patrol_create.png',
    },
    {
      id: 'fog',
      name: 'Fog',
      selected: false,
      icon: 'assets/waypoint_icons/fog_create.png',
    },
  ];

  // ── Wizard row art ────────────────────────────────────────────────────────
  // Every list row in the creation wizard uses the app's dedicated
  // `assets/images/<name>_create.png` set, resolved exactly like Flutter's
  // `_createRowIcon(name)`. Those 19 files are now vendored into
  // `assets/waypoint_icons/`; before that the web substituted whatever
  // general-purpose logo was to hand (map-filter glyphs, a species teardrop
  // standing in for a whole fish family), which is why these rows never matched
  // the phone. `fallbackIcon` keeps the old art as a safety net per row.

  /** Top-level rows: Alerts / Places / Events (order matches the app). */
  readonly topLevelOptions: WizardOption[] = [
    { id: 'alerts', label: 'Alerts', icon: 'assets/waypoint_icons/alerts_create.png', fallbackIcon: '/assets/home/wavepoint/hazard_logo.svg' },
    { id: 'places', label: 'Places', icon: 'assets/waypoint_icons/mark_places_create.png', fallbackIcon: '/assets/home/wavepoint/canyon_logo.svg' },
    // The app has no `events_create.png` and falls back to a generated hook
    // badge; Fish Caught's art is the closest equivalent we can actually show.
    { id: 'events', label: 'Events', icon: 'assets/waypoint_icons/fish_caught_create.png', fallbackIcon: '/assets/home/wavepoint/fishcaugh_logo.svg', anchor: 'waypoint.eventsOption' },
  ];

  /** Events subtype rows. Fish Caught opens the family → species chain. */
  readonly eventsSubtypeOptions: WizardOption[] = [
    { id: 'fish_caught', label: 'Fish Caught', icon: 'assets/waypoint_icons/fish_caught_create.png', fallbackIcon: '/assets/home/wavepoint/fishcaugh_logo.svg', anchor: 'waypoint.fishCaughtOption' },
    { id: 'fish_mark', label: 'Fish Mark', icon: 'assets/waypoint_icons/fish_mark_create.png', fallbackIcon: '/assets/home/filter/fishmark_logo.svg' },
    { id: 'bait_mark', label: 'Bait Mark', icon: 'assets/waypoint_icons/bait_mark_create.png', fallbackIcon: '/assets/home/wavepoint/baitmark_logo.svg' },
    { id: 'temp_break', label: 'Temp Break', icon: 'assets/waypoint_icons/temp_break_create.png', fallbackIcon: '/assets/home/wavepoint/tempbreak_logo.svg' },
  ];

  /** Fish family rows, "Others" last — the app's `tuna|billfish|pelagic|inshore` art. */
  readonly fishFamilyOptions: WizardOption[] = [
    { id: 'tuna', label: 'Tuna Caught', icon: 'assets/waypoint_icons/tuna_create.png', fallbackIcon: 'assets/waypoint_icons/caught_yellowfin.png', anchor: 'waypoint.tunaOption' },
    { id: 'billfish', label: 'Billfish Caught', icon: 'assets/waypoint_icons/billfish_create.png', fallbackIcon: 'assets/waypoint_icons/caught_bluemarlin.png' },
    { id: 'pelagic', label: 'Pelagic Caught', icon: 'assets/waypoint_icons/pelagic_create.png', fallbackIcon: 'assets/waypoint_icons/caught_mahi.png' },
    { id: 'inshore', label: 'Inshore Caught', icon: 'assets/waypoint_icons/inshore_create.png', fallbackIcon: 'assets/waypoint_icons/caught_stripedbass.png' },
    { id: 'other', label: 'Others', icon: 'assets/waypoint_icons/fish_caught_create.png', fallbackIcon: 'assets/waypoint_icons/caught_other.png' },
  ];

  /** Patrol jurisdiction rows (Alerts → Patrol). */
  readonly patrolJurisdictionOptions: WizardOption[] = [
    { id: 'state', label: 'State', icon: 'assets/waypoint_icons/patrol_create.png', fallbackIcon: '/assets/home/wavepoint/waypoint_patrol.svg' },
    { id: 'federal', label: 'Federal', icon: 'assets/waypoint_icons/patrol_create.png', fallbackIcon: '/assets/home/wavepoint/waypoint_patrol.svg' },
  ];

  constructor(
    private submenuService: SubMenuService,
    private waypointService: WaypointService,
    private tripService: TripService,
    private rideAlongService: RideAlongService,
    private vesselService: VesselService,
    private authService: AuthService,
    private userProfileService: UserProfileService,
    public socialService: SocialService,
    private cdr: ChangeDetectorRef,
    private clarityService: ClarityService,
    public toast: ToastService,
    private tourController: TourController,
  ) { }

  // ── Photo step ────────────────────────────────────────────────────────────

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const files = Array.from(input.files);
    const room = Math.max(0, this.maxMediaItems - this.existingImageUrls.length);
    const newMedia = files.map(file => ({
      file,
      url: URL.createObjectURL(file)
    }));

    this.selectedMedia = [...this.selectedMedia, ...newMedia].slice(0, room);

    // Reset input value to allow selecting same file again if removed
    input.value = '';
  }

  removeMedia(index: number) {
    if (this.selectedMedia[index]) {
      URL.revokeObjectURL(this.selectedMedia[index].url);
      const updatedMedia = [...this.selectedMedia];
      updatedMedia.splice(index, 1);
      this.selectedMedia = updatedMedia;
    }
  }

  /** Edit mode: queue an already-uploaded photo for deletion on submit. */
  removeExistingMedia(index: number) {
    const url = this.existingImageUrls[index];
    if (!url) return;
    this.existingImageUrls = this.existingImageUrls.filter((_, i) => i !== index);
    this.removedImageUrls.push(url);
  }

  get totalImageCount(): number {
    return this.existingImageUrls.length + this.selectedMedia.length;
  }

  get canAddMoreMedia(): boolean {
    return this.totalImageCount < this.maxMediaItems;
  }

  ngOnInit() {
    // Keep local copy of pre-fetched phone GPS in sync — mirrors Flutter's _phoneGpsPosition
    this.submenuService.userGpsPosition$.subscribe((pos) => {
      this.userGpsPosition = pos;
    });

    this.submenuService.waypointModalState$.subscribe((state: boolean) => {
      const wasClosed = !this.isOpen;
      this.isOpen = state;

      if (state && wasClosed) {
        const openingCoords = this.requestedCoords ? { ...this.requestedCoords } : null;
        this.resetWizardState();

        // Edit target is published before the modal state flips (see
        // SubMenuService.openWaypointEditor), so it's readable synchronously here.
        const editTarget = this.submenuService.getWaypointEditTarget();
        if (editTarget) {
          this.seedFromEditWaypoint(editTarget);
          return;
        }

        this.isResolvingCoordinates = true;
        this.fetchVesselData();
        this.fetchDefaultSharing();
        this.resolveLocation(openingCoords);
      }

      // Paint the freshly-reset sheet immediately.
      //
      // This subject is pushed from map handlers that run OUTSIDE Angular's zone
      // (home-page creates the map with `runOutsideAngular`), so the emission
      // that opens the wizard can arrive with no change-detection pass behind it.
      // The edit path got one via `seedFromEditWaypoint`; the create path had
      // none, leaving the first step rendered from stale state until some other
      // event happened to tick the zone — the "not very responsive on first
      // click" report.
      this.cdr.detectChanges();
    });

    this.submenuService.waypointCoordinates$.subscribe((coords: { lat: number; lng: number } | null) => {
      this.requestedCoords = coords ? { ...coords } : null;
      if (this.isOpen && !this.isEdit) {
        this.resolveLocation(this.requestedCoords);
      }
    });
  }

  /** Wipes every per-opening field so nothing leaks between sheet openings. */
  private resetWizardState() {
    this.selectedMedia.forEach((m) => URL.revokeObjectURL(m.url));
    this.selectedMedia = [];
    this.existingImageUrls = [];
    this.removedImageUrls = [];
    this.editWaypoint = null;
    this.restrictedFleetRef = null;
    this.description = '';
    this.currentDate = new Date();
    this.fishQuantity = 1;
    this.placeName = '';
    this.showIcon = true;
    this.isCreatingWaypoint = false;
    this.isDeleting = false;
    this.showDeleteConfirm = false;
    this.editSharedWithLoaded = true;
    this.conditions = {};
    this.dataBridgeEnabled = false;
    this.baselineDataBridgeEnabled = false;
    this.hasFreshGNSS = false;
    this.coords = null;
    this.originalCoords = null;
    this.showCoordPicker = false;
    this.pickedCoords = null;
    this.pickedCoordsDisplay = '';
    this.visibility = 'private';
    this.eventTypes.forEach((type) => type.selected = false);
    this.placeTypes.forEach((type) => type.selected = false);
    this.alertTypes.forEach((type) => type.selected = false);
    this.fishTypes.forEach((type) => type.selected = false);
    this.fleets = [];
    this.dockmates = [];
    this.visibilitySearchQuery = '';
    this.isResolvingCoordinates = false;

    // Wizard starts at the type picker with nothing chosen.
    this.currentStep = 'topLevel';
    this.history = [];
    this.topLevelCategory = '';
    this.eventsSubtype = '';
    this.fishFamily = '';
    this.patrolJurisdiction = null;
  }

  ngOnDestroy() {
    this.coordPickerMap?.remove();
    this.coordPickerMap = null;
    this.selectedMedia.forEach((media) => URL.revokeObjectURL(media.url));
  }

  get isEdit(): boolean {
    return !!this.editWaypoint;
  }

  /**
   * Lift the WHOLE component above the profile panel while editing.
   *
   * `:host` is `position: fixed; z-index: 4000`, which makes it a stacking
   * context — so `.bottom-sheet-overlay--edit`'s `z-index: 6000` was measured
   * against its siblings inside this host, not against the page. The profile
   * panel that launches Edit sits at 5000, so the sheet opened correctly and
   * then rendered entirely behind the panel: the Edit button looked dead.
   * A z-index on the host itself is the only thing that can clear 5000.
   */
  @HostBinding('class.is-edit') get hostIsEdit(): boolean {
    return this.isEdit;
  }

  private fetchDefaultSharing() {
    const userId = this.authService.getUserIdFromToken();
    if (!userId) return;

    this.userProfileService.getProfileById(userId).subscribe({
      next: (res: any) => {
        if (res && res.settings && res.settings.privacy) {
          const privacy = res.settings.privacy;
          // Mirror Settings tab values:
          // 0 = public, 1 = private, 2 = custom (Pier Circle)
          if (privacy.default_visibility === 0) this.visibility = 'public';
          else if (privacy.default_visibility === 1) this.visibility = 'private';
          else if (privacy.default_visibility === 2) this.visibility = 'custom';

          // Pre-fetch fleets/friends if we have default IDs
          if (privacy.default_fleet_ids?.length > 0 || privacy.default_friend_ids?.length > 0) {
            this.fetchFleetsAndFriends(privacy.default_fleet_ids, privacy.default_friend_ids);
          }
        }
      }
    });
  }

  private resolveLocation(requestedCoords: { lat: number; lng: number } | null = null) {
    this.fetchPrimaryVesselCoords().subscribe({
      next: (vesselData) => {
        this.applyResolvedOpeningCoords(vesselData, requestedCoords);
      },
      error: () => {
        this.applyResolvedOpeningCoords(null, requestedCoords);
      }
    });
  }

  private applyResolvedOpeningCoords(
    vesselData: { lat: number; lng: number; isStale: boolean } | null,
    requestedCoords: { lat: number; lng: number } | null
  ) {
    if (requestedCoords) {
      this.updateCoords(requestedCoords.lat, requestedCoords.lng);
      return;
    }

    if (vesselData && !vesselData.isStale) {
      // Tier 1: Fresh vessel observation (<= 15 minutes) — use vessel coords.
      this.updateCoords(vesselData.lat, vesselData.lng);
      return;
    }

    if (this.userGpsPosition) {
      // Tier 2: Stale or missing vessel observation — force current GPS.
      this.updateCoords(this.userGpsPosition.lat, this.userGpsPosition.lng);
      return;
    }

    // Tier 3: GPS not yet available — request it live from browser.
    this.fallbackToBrowserLocation();
  }

  private updateCoords(lat: number, lng: number) {
    this.coords = { lat, lng };
    this.captureOriginalCoords(lat, lng);
    this.currentDate = new Date();
    this.isResolvingCoordinates = false;
    this.cdr.detectChanges();
    this.fetchConditions(lat, lng);
  }

  private captureOriginalCoords(lat: number, lng: number) {
    if (!this.originalCoords) {
      this.originalCoords = { lat, lng };
      this.baselineDataBridgeEnabled = this.dataBridgeEnabled;
    }
  }

  private fallbackToBrowserLocation() {
    if (!navigator.geolocation) {
      this.isResolvingCoordinates = false;
      this.showError('Location services not supported by this browser');
      return;
    }

    // No maximumAge (always fresh), no timeout (wait as long as needed — mirrors Flutter's
    // Geolocator.getCurrentPosition with no timeout). cdr.detectChanges() ensures Angular
    // renders the new coords immediately even though the callback runs outside the zone.
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.updateCoords(pos.coords.latitude, pos.coords.longitude);
        this.cdr.detectChanges();
      },
      (err) => {
        console.warn('[Waypoint] GPS failed (code', err.code, '):', err.message);
        if (err.code === 1 /* PERMISSION_DENIED */) {
          this.showError('Location permission denied. Please allow location access in your browser.');
        } else {
          this.showError('Unable to determine your GPS location. Please try again.');
        }
        this.isResolvingCoordinates = false;
        this.cdr.detectChanges();
      },
      { enableHighAccuracy: false }
    );
  }


  close() {
    this.isCreatingWaypoint = false;
    this.submenuService.setWaypointModalState(false);
  }

  get isPositionEdited(): boolean {
    if (!this.coords || !this.originalCoords) return false;
    return Math.abs(this.coords.lat - this.originalCoords.lat) > 0.000001
      || Math.abs(this.coords.lng - this.originalCoords.lng) > 0.000001;
  }

  openCoordPicker() {
    if (!this.coords) return;
    this.showCoordPicker = true;
    setTimeout(() => this.initCoordPickerMap(), 50);
  }

  private initCoordPickerMap() {
    this.coordPickerMap?.remove();
    const center: [number, number] = this.coords
      ? [this.coords.lng, this.coords.lat]
      : [78.9629, 20.5937];

    this.coordPickerMap = new maplibregl.Map({
      container: 'create-coord-picker-map',
      style: `https://api.maptiler.com/maps/019adf7b-1688-7ff4-ae90-c50ef00338f9/style.json?key=${environment.maptilerKey}`,
      center,
      zoom: this.coords ? 13 : 4,
    });

    // Anchor zoom to the crosshair (map center) instead of the cursor so the
    // picked point doesn't drift to a different location when zooming.
    this.coordPickerMap.doubleClickZoom.disable();
    this.coordPickerMap.scrollZoom.disable();
    this.coordPickerMap.getCanvas().addEventListener('wheel', this.onPickerWheelZoom, { passive: false });

    this.updatePickedCoordsFromCenter();
    this.coordPickerMap.on('move', () => this.updatePickedCoordsFromCenter());
  }

  // Wheel zoom anchored to the map center (the crosshair) instead of the cursor.
  private onPickerWheelZoom = (e: WheelEvent) => {
    e.preventDefault();
    const map = this.coordPickerMap;
    if (!map) return;
    const delta = e.deltaY > 0 ? -0.5 : 0.5;
    map.zoomTo(map.getZoom() + delta, { duration: 120 });
  };

  private updatePickedCoordsFromCenter() {
    if (!this.coordPickerMap) return;
    const c = this.coordPickerMap.getCenter();
    this.pickedCoords = { lat: c.lat, lng: c.lng };
    this.pickedCoordsDisplay = `${this.formatCoordinate(c.lat, 'lat')} ${this.formatCoordinate(c.lng, 'lng')}`;
    this.cdr.detectChanges();
  }

  zoomInPicker() {
    this.coordPickerMap?.zoomIn();
  }

  zoomOutPicker() {
    this.coordPickerMap?.zoomOut();
  }

  // Instant move (jumpTo) to the device's GPS position. Tries high accuracy, then
  // falls back to a quick low-accuracy fix; surfaces an error if both fail.
  useCurrentLocationPicker() {
    if (this.locatingMe) return;
    if (!navigator.geolocation) {
      this.showError('Location services not supported by this browser');
      this.cdr.detectChanges();
      return;
    }
    this.locatingMe = true;

    const onSuccess = (pos: GeolocationPosition) => {
      this.locatingMe = false;
      const map = this.coordPickerMap;
      if (!map) return;
      map.jumpTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 16 });
      this.updatePickedCoordsFromCenter();
    };

    navigator.geolocation.getCurrentPosition(
      onSuccess,
      () => {
        navigator.geolocation.getCurrentPosition(
          onSuccess,
          (err) => {
            this.locatingMe = false;
            this.showError(
              err.code === err.PERMISSION_DENIED
                ? 'Location permission denied. Please allow location access in your browser.'
                : 'Unable to determine your GPS location. Please try again.'
            );
            this.cdr.detectChanges();
          },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
        );
      },
      { enableHighAccuracy: true, timeout: 6000 }
    );
  }

  confirmCoordPicker() {
    if (!this.pickedCoords) return;
    this.coords = { ...this.pickedCoords };
    this.currentDate = new Date();
    this.fetchConditions(this.pickedCoords.lat, this.pickedCoords.lng);

    if (this.isPositionEdited) {
      this.dataBridgeEnabled = false;
    }

    this.closeCoordPicker();
  }

  undoCoordinates() {
    if (!this.originalCoords) return;
    this.coords = { ...this.originalCoords };
    this.currentDate = new Date();
    this.fetchConditions(this.originalCoords.lat, this.originalCoords.lng);
    this.dataBridgeEnabled = this.baselineDataBridgeEnabled;
  }

  closeCoordPicker() {
    this.coordPickerMap?.remove();
    this.coordPickerMap = null;
    this.showCoordPicker = false;
  }

  private fetchVesselData() {
    this.vesselService.getVessels().pipe(
      map((res: any) => {
        const vessels = (res && res.results) || res || [];
        return vessels.find((v: any) => v.is_primary === true);
      }),
      switchMap((primary: any) => {
        if (!primary) return of(null);
        const vesselId = primary._id || primary.id;
        if (!vesselId) return of(null);

        // Fetch observations
        this.vesselService.getLatestObservation(vesselId).subscribe({
          next: (obs: any) => this.mergeObservation(obs),
          error: () => console.log('No latest observation found')
        });

        return of(primary);
      }),
      take(1)
    ).subscribe();
  }

  private fetchPrimaryVesselCoords(): Observable<{ lat: number; lng: number, isStale: boolean } | null> {
    return this.vesselService.getVessels().pipe(
      map((res: any) => {
        const vessels = (res && res.results) || res || [];
        return vessels.find((v: any) => v.is_primary === true);
      }),
      switchMap((primary: any) => {
        if (!primary) return of(null);

        const vesselId = primary._id || primary.id;
        if (!vesselId) return of(null);

        // Observations should be fetched on modal open, but we skip background subscribe here
        // as it's now handled by fetchVesselData() when opening.

        return this.vesselService.getLatestCoords(vesselId).pipe(
          map(newCoords => {
            if (newCoords && newCoords.lat != null && newCoords.lon != null) {
              const ts = newCoords.ts && !newCoords.ts.endsWith('Z') ? newCoords.ts + 'Z' : newCoords.ts;
              const coordTime = new Date(ts).getTime();
              const now = new Date().getTime();
              const diffMinutes = (now - coordTime) / (1000 * 60);

              return {
                lat: newCoords.lat,
                lng: newCoords.lon,
                isStale: diffMinutes > this.freshObservationThresholdMinutes
              };
            }
            return null;
          }),
          catchError(() => of(null))
        );
      }),
      catchError(() => of(null)),
      take(1)
    );
  }

  fetchConditions(lat: number, lon: number) {
    this.conditionsLoading = true;
    this.waypointService.getConditions(lat, lon).subscribe({
      next: (res) => {
        if (!this.conditions) this.conditions = {};

        if (res.air_temperature !== undefined) this.conditions.air_temperature = res.air_temperature;
        if (res.air_pressure !== undefined) this.conditions.air_pressure = res.air_pressure;
        if (res.precipitation !== undefined) this.conditions.precipitation = res.precipitation;
        if (res.wind_speed !== undefined) this.conditions.wind_speed = res.wind_speed;
        if (res.wave_height !== undefined) this.conditions.wave_height = res.wave_height;
        if (res.wave_period !== undefined) this.conditions.wave_period = res.wave_period;

        if (res.air_temperature_unit) this.conditions.air_temperature_unit = res.air_temperature_unit;
        if (res.depth_unit) this.conditions.depth_unit = res.depth_unit;
        if (res.wind_speed_unit) this.conditions.wind_speed_unit = res.wind_speed_unit;

        this.conditionsLoading = false;
      },
      error: (err) => {
        console.error('Error fetching conditions:', err);
        this.conditionsLoading = false;
      }
    });
  }

  mergeObservation(obs: any) {
    if (!obs || !obs.pgns || obs.message) {
      this.dataBridgeEnabled = false;
      return;
    }

    if (!this.conditions) this.conditions = {};

    let hasFreshGNSS = false;
    let latestTs: string | null = null;

    obs.pgns.forEach((p: any) => {
      // Sanitize timestamp: API gives UTC but often lacks 'Z'
      const ts = p.ts_utc && !p.ts_utc.endsWith('Z') ? p.ts_utc + 'Z' : p.ts_utc;
      if (ts && (!latestTs || new Date(ts) > new Date(latestTs))) {
        latestTs = ts;
      }

      const f = p.fields || {};

      switch (p.name) {
        case 'GNSSPositionData':
          // Fresh Location Injection: only use if data is fresh
          const coordTime = ts ? new Date(ts).getTime() : 0;
          const freshness = (new Date().getTime() - coordTime) / (1000 * 60);

          if (freshness <= 15) {
            hasFreshGNSS = true;
            if (!this.coords && f.lat_deg != null && f.lon_deg != null) {
              console.log('Location injected from fresh observation GNSS.');
              this.updateCoords(f.lat_deg, f.lon_deg);
            }
          }
          break;
        case 'COGSOGRapidUpdate':
          if (f.cog_deg != null) this.conditions.cog = f.cog_deg;
          if (f.sog != null) {
            // sog is usually in m/s, convert to kts (1 m/s = 1.94384 kts)
            this.conditions.sog = f.sog * 1.94384;
          }
          break;
        case 'VesselHeading':
          if (f.heading_deg != null) this.conditions.heading = f.heading_deg;
          break;
        case 'Temperature':
          // We might have multiple temps. Often p.src or p.instance tells which is which.
          // For simplicity, if we have water temp PGN or instance, we map it.
          if (f.temp_c != null) {
            const tempF = (f.temp_c * 9 / 5) + 32;
            // Guessing: small src or name mapping. In user JSON, all are 18.3C
            if (p.name === 'Temperature') this.conditions.temp = tempF;
            else this.conditions.air_temperature = tempF;
          }
          break;
        case 'WaterDepth':
          if (f.depth_m != null) {
            this.conditions.depth = f.depth_m * 3.28084;
          }
          break;
      }
    });

    this.hasFreshGNSS = hasFreshGNSS;

    if (latestTs) {
      const coordTime = new Date(latestTs).getTime();
      const now = new Date().getTime();
      const diffMinutes = (now - coordTime) / (1000 * 60);

      // Fresh GNSS makes Data Bridge available, but the create flow should
      // still default to OFF until the user explicitly enables it.
      if (this.hasFreshGNSS && diffMinutes <= 15) {
        this.dataBridgeEnabled = false;
        this.baselineDataBridgeEnabled = false;
        this.conditions.ts_utc = latestTs;
      } else {
        this.dataBridgeEnabled = false;
        // If stale or no fresh GNSS, ensure we don't show the old timestamp OR old vessel data
        this.conditions.ts_utc = null;
        this.conditions.cog = null;
        this.conditions.sog = null;
        this.conditions.temp = null;
        this.conditions.depth = null;
        this.conditions.heading = null;

        console.log(`Vessel data restricted. Fresh GNSS: ${this.hasFreshGNSS}, Stale: ${diffMinutes.toFixed(1)} mins old.`);
      }
    } else {
      this.dataBridgeEnabled = false;
      this.hasFreshGNSS = false;
      this.conditions.ts_utc = null;
      // Also clear vessel fields if no PGNs have timestamps (unlikely but safe)
      this.conditions.cog = null;
      this.conditions.sog = null;
      this.conditions.water_temp = null;
      this.conditions.water_depth = null;
      this.conditions.heading = null;
    }
  }

  selectChip(type: WaypointType, category: string) {
    if (category === 'eventTypes') {
      this.placeTypes.forEach(t => t.selected = false);
      this.alertTypes.forEach(t => t.selected = false);
      this.eventTypes.forEach(t => t.selected = false);
      type.selected = true;
      this.dataBridgeEnabled = false;

      if (this.isFishCaughtSelected()) {
        this.selectDefaultFishType();
      } else {
        this.fishTypes.forEach(t => t.selected = false);
      }
    } else if (category === 'fishTypes') {
      this.placeTypes.forEach(t => t.selected = false);
      this.alertTypes.forEach(t => t.selected = false);
      const previouslySelectedEvent = this.selectedEventType;
      this.eventTypes.forEach(t => t.selected = false);
      const fc = previouslySelectedEvent || this.eventTypes.find(t => t.id === 'tuna');
      if (fc) fc.selected = true;

      // Species is a required single-select once Fish Caught is the event, so a
      // tap on the already-selected chip re-affirms it instead of clearing the
      // group (matches the app's species list, which can't be unselected).
      this.fishTypes.forEach(t => t.selected = false);
      type.selected = true;
    } else if (category === 'placeTypes') {
      this.eventTypes.forEach(t => t.selected = false);
      this.fishTypes.forEach(t => t.selected = false);
      this.alertTypes.forEach(t => t.selected = false);
      this.placeTypes.forEach(t => t.selected = false);
      type.selected = true;
      this.dataBridgeEnabled = false;
    } else if (category === 'alertTypes') {
      this.eventTypes.forEach(t => t.selected = false);
      this.fishTypes.forEach(t => t.selected = false);
      this.placeTypes.forEach(t => t.selected = false);
      this.alertTypes.forEach(t => t.selected = false);
      type.selected = true;
      this.dataBridgeEnabled = false;
      this.visibility = 'public';
    }
  }

  isFishCaughtSelected(): boolean {
    return !!this.selectedEventType?.payloadType && this.selectedEventType.payloadType === this.fishCaughtTypeId;
  }

  /** True for the family-less "Others" catch (no species, no fish_category). */
  get isFishCaughtOther(): boolean {
    return this.selectedEventType?.id === this.fishCaughtOtherId;
  }

  private selectDefaultFishType(): void {
    const allowed = new Set(this.getAllowedFishSubtypeIds());
    let selectedStillValid = false;
    this.fishTypes.forEach((type) => {
      const shouldSelect = allowed.has(type.id) && type.id === this.defaultFishTypeId;
      if (type.selected && allowed.has(type.id)) {
        selectedStillValid = true;
      }
      type.selected = shouldSelect;
    });
    if (!selectedStillValid && !this.selectedFishType) {
      // First allowed id = the family's default species (see getAllowedFishSubtypeIds).
      const fallbackId = this.getAllowedFishSubtypeIds()[0];
      const fallback = this.fishTypes.find((type) => type.id === fallbackId);
      if (fallback) fallback.selected = true;
    }
  }

  get selectedEventType(): WaypointType | null {
    return this.eventTypes.find((t) => t.selected) || null;
  }

  get selectedPrimaryType(): WaypointType | null {
    return (
      this.alertTypes.find((t) => t.selected) ||
      this.placeTypes.find((t) => t.selected) ||
      this.selectedEventType ||
      null
    );
  }

  get selectedPrimaryCategory(): 'event' | 'place' | 'alert' | null {
    const selectedType = this.selectedPrimaryType;
    if (!selectedType) return null;
    if (this.alertTypes.includes(selectedType)) return 'alert';
    if (this.placeTypes.includes(selectedType)) return 'place';
    if (this.eventTypes.includes(selectedType)) return 'event';
    return null;
  }

  get selectedFishType(): WaypointSubtype | null {
    return this.fishTypes.find((t) => t.selected) || null;
  }

  get selectedWaypointTitle(): string {
    const selectedType = this.selectedPrimaryType;
    if (!selectedType) return 'Waypoint';
    if (this.isFishCaughtSelected()) {
      if (this.isFishCaughtOther) return 'Fish Caught';
      return this.formatFishCaughtTitle(this.selectedFishType?.id, this.selectedEventType?.categoryKey, selectedType.name);
    }
    if (this.usesCustomTitle && this.placeName.trim()) {
      return this.placeName.trim();
    }
    return selectedType.name;
  }

  /** Map-pin art (same as Flutter's location tile / map), not the round row badge. */
  get selectedTypeIcon(): string {
    if (this.isFishCaughtSelected()) {
      return getWaypointPinAsset({ type: 'fish_caught', details: { fish_subtype: this.selectedFishType?.id } }, false);
    }
    const id = this.selectedPrimaryType?.id || '';
    if (id === 'canyon') return 'assets/waypoint_icons/pins/canyon.png';
    if (hasWaypointPinArt(id)) return getWaypointPinAsset({ type: id }, false);
    return this.selectedPrimaryType?.icon || 'assets/home/wavepoint/wavepoint_logo.svg';
  }

  /** Spot pin = canyon.png with its triangle covered and the X drawn on top (Flutter SpotThumbnailPin). */
  get isSpotSelected(): boolean {
    return !this.isFishCaughtSelected() && this.selectedPrimaryType?.id === 'canyon';
  }

  get canCreateWaypoint(): boolean {
    return !this.isCreatingWaypoint && !this.isResolvingCoordinates && !!this.coords;
  }

  isPlaceTypeSelected(): boolean {
    return this.placeTypes.some((type) => type.selected);
  }

  isAlertTypeSelected(): boolean {
    return this.alertTypes.some((type) => type.selected);
  }

  isSpotOrInletSelected(): boolean {
    return !!this.placeTypes.find((type) => type.selected && (type.id === 'canyon' || type.id === 'inlet'));
  }

  /** Places and Alerts both accept a custom name on the details step. */
  get usesCustomTitle(): boolean {
    return this.topLevelCategory === 'places' || this.topLevelCategory === 'alerts';
  }

  incrementFishQuantity(step: number): void {
    this.fishQuantity = this.normalizeFishQuantity(this.fishQuantity + step);
  }

  onFishQuantityInput(value: string): void {
    const digitsOnly = String(value ?? '').replace(/[^\d]/g, '').slice(0, 2);
    if (!digitsOnly) {
      this.fishQuantity = 1;
      return;
    }
    this.fishQuantity = this.normalizeFishQuantity(Number(digitsOnly));
  }

  private formatFishCaughtTitle(subtypeId?: string, categoryKey?: string, fallback = 'Fish Caught'): string {
    const subtype = String(subtypeId || '').trim().toLowerCase();
    if (!subtype) return fallback;
    if (subtype.startsWith('general_')) {
      const categoryName = String(categoryKey || subtype.replace(/^general_/, '')).replace(/[_-]+/g, ' ');
      return `${this.titleCase(categoryName)} Caught`;
    }
    const match = this.fishTypes.find((type) => type.id === subtype);
    return `${match?.name || this.titleCase(subtype.replace(/[_-]+/g, ' '))} Caught`;
  }

  private titleCase(value: string): string {
    return value
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private normalizeFishQuantity(value: number): number {
    if (!Number.isFinite(value)) return 1;
    return Math.max(1, Math.min(99, Math.trunc(value)));
  }

  /**
   * Species for a family, in the order the app lists them
   * (`FishCaughtConfig.categories`). The first entry is that family's default
   * species, so [selectDefaultFishType]'s fallback lands where the app does.
   */
  private getAllowedFishSubtypeIds(): string[] {
    switch (this.selectedEventType?.categoryKey) {
      case 'billfish':
        return ['bluemarlin', 'whitemarlin', 'sailfish', 'swordfish', 'general_billfish'];
      case 'pelagic':
        return ['mahi', 'wahoo', 'general_pelagic'];
      case 'inshore':
        return ['stripedbass', 'fluke', 'seabass', 'bluefish', 'tautog', 'general_inshore'];
      case 'tuna':
      default:
        return ['yellowfin', 'bluefin', 'bigeye', 'albacore', 'general_tuna'];
    }
  }

  get visibleFishTypes(): WaypointSubtype[] {
    // Walk the allowed ids (not the catalog) so rows render in the app's order.
    return this.getAllowedFishSubtypeIds()
      .map((id) => this.fishTypes.find((type) => type.id === id))
      .filter((type): type is WaypointSubtype => !!type);
  }

  /** Species rows show the teardrop art, falling back to the round tab badge. */
  speciesRowIcon(type: WaypointSubtype): string {
    return `assets/waypoint_icons/caught_${type.id}.png`;
  }

  /** Swap a missing asset for its fallback rather than showing a broken image. */
  onRowIconError(event: Event, fallback: string) {
    const img = event.target as HTMLImageElement;
    if (!img || img.dataset['fellBack'] === '1') return;
    img.dataset['fellBack'] = '1';
    img.src = fallback;
  }

  // ── Wizard navigation ─────────────────────────────────────────────────────

  /**
   * Next step for [step] given the selections so far. The flat top level routes
   * into each type's chain; every branch converges on photo → details →
   * privacy. Alerts stop at details (always public, no visibility step).
   * Returning [step] itself means "terminal — submit via the header ✓".
   */
  nextStepFrom(step: WizardStep): WizardStep {
    switch (step) {
      case 'changeLocation':
        // Edit-only leading step → into the normal type chain.
        return 'topLevel';
      case 'topLevel':
        switch (this.topLevelCategory) {
          case 'alerts': return 'alertsSubtype';
          case 'places': return 'placesSubtype';
          case 'events': return 'eventsSubtype';
          default: return 'photo';
        }
      case 'eventsSubtype':
        // Fish Caught opens the family chain; marks/temp-break are complete
        // types → straight to photo.
        return this.eventsSubtype === 'fish_caught' ? 'fishFamily' : 'photo';
      case 'fishFamily':
        // Named family → species; "Others" (generic Fish Caught) skips species
        // but still collects a quantity.
        return this.fishFamily === 'other' ? 'fishQuantity' : 'fishSpecies';
      case 'fishSpecies':
        return 'fishQuantity';
      case 'fishQuantity':
        return 'photo';
      case 'placesSubtype':
        return 'photo';
      case 'alertsSubtype':
        return this.selectedPrimaryType?.id === 'patrol' ? 'patrolJurisdiction' : 'photo';
      case 'patrolJurisdiction':
        return 'photo';
      case 'photo':
        return 'details';
      case 'details':
        // Alerts are always public → no visibility step.
        return this.topLevelCategory === 'alerts' ? 'details' : 'privacy';
      case 'privacy':
        // Privacy is the final step; submit is via the header ✓.
        return 'privacy';
    }
  }

  advanceFromStep(step: WizardStep) {
    const next = this.nextStepFrom(step);
    if (next === step) return;
    this.history.push(step);
    this.currentStep = next;
    this.onStepEntered();
  }

  goBack() {
    // Mid-submit, block step navigation so the user can't unwind state while a
    // create/update is in flight.
    if (this.isCreatingWaypoint) return;
    if (this.history.length === 0) {
      this.close();
      return;
    }
    this.currentStep = this.history.pop()!;
    this.onStepEntered();
  }

  /** Lazy-loads whatever the newly-entered step needs. */
  private onStepEntered() {
    if (this.currentStep === 'privacy' && this.fleets.length === 0 && this.dockmates.length === 0) {
      this.fetchFleetsAndFriends();
    }
    this.cdr.detectChanges();
  }

  /**
   * Every row tap runs through here so the selection paints on the FIRST click.
   * The handlers below either auto-advance (which repaints via `onStepEntered`)
   * or, on a terminal step, only mutate `selected` — and that mutation may arrive
   * outside the zone, leaving the tick unrendered until the next unrelated event.
   */
  private repaint(): void {
    this.cdr.detectChanges();
  }

  // ── Wizard selection handlers ─────────────────────────────────────────────
  //
  // Each handler updates state, resets downstream selections that no longer
  // apply, and auto-advances (selection steps have no Continue button).

  selectTopLevel(category: string) {
    const changed = this.topLevelCategory !== category;
    this.topLevelCategory = category;
    if (changed) {
      this.eventsSubtype = '';
      this.fishFamily = '';
      this.patrolJurisdiction = null;
      this.placeName = '';
      this.fishQuantity = 1;
      this.eventTypes.forEach(t => t.selected = false);
      this.placeTypes.forEach(t => t.selected = false);
      this.alertTypes.forEach(t => t.selected = false);
      this.fishTypes.forEach(t => t.selected = false);
    }
    this.advanceFromStep('topLevel');
  }

  selectEventsSubtype(key: string) {
    const changed = this.eventsSubtype !== key;
    this.eventsSubtype = key;
    if (changed) {
      this.fishFamily = '';
      this.fishQuantity = 1;
      this.fishTypes.forEach(t => t.selected = false);
      const complete = this.eventTypes.find((t) => t.id === key);
      if (complete) {
        // Fish Mark / Bait Mark / Temp Break are complete types here.
        this.selectChip(complete, 'eventTypes');
      } else {
        // fish_caught — family chosen next.
        this.eventTypes.forEach(t => t.selected = false);
      }
    }
    this.advanceFromStep('eventsSubtype');
  }

  selectFishFamily(familyId: string) {
    this.fishFamily = familyId;
    const catalogId = familyId === 'other' ? this.fishCaughtOtherId : familyId;
    const entry = this.eventTypes.find((t) => t.id === catalogId);
    if (entry) {
      this.selectChip(entry, 'eventTypes');
      // "Others" carries no species — selectChip seeds a default for named
      // families only.
      if (familyId === 'other') this.fishTypes.forEach(t => t.selected = false);
    }
    this.advanceFromStep('fishFamily');
  }

  selectFishSpecies(type: WaypointSubtype) {
    this.selectChip(type, 'fishTypes');
    this.advanceFromStep('fishSpecies');
  }

  selectPlacesSubtype(type: WaypointType) {
    const changed = !type.selected;
    this.selectChip(type, 'placeTypes');
    if (changed) this.placeName = '';
    this.advanceFromStep('placesSubtype');
  }

  selectAlertsSubtype(type: WaypointType) {
    const changed = !type.selected;
    this.selectChip(type, 'alertTypes');
    if (changed) {
      this.placeName = '';
      if (type.id !== 'patrol') this.patrolJurisdiction = null;
    }
    this.advanceFromStep('alertsSubtype');
  }

  selectPatrolJurisdiction(key: string) {
    this.patrolJurisdiction = key;
    this.advanceFromStep('patrolJurisdiction');
  }

  // ── Step metadata (header subtitle, gating, progress) ──────────────────────

  /** Header subtitle telling the user where they are in the flow. */
  get stepSubtitle(): string {
    switch (this.currentStep) {
      case 'changeLocation': return 'Change location';
      case 'topLevel': return 'Select a type';
      case 'eventsSubtype': return 'Events';
      case 'fishFamily': return 'Fish Caught';
      case 'fishSpecies':
        // Header reads as the chosen family, e.g. "Tuna Caught".
        return this.selectedEventType?.categoryKey ? this.selectedEventType.name : 'Fish Caught';
      case 'fishQuantity': return 'Quantity';
      case 'placesSubtype': return 'Mark Places';
      case 'alertsSubtype': return 'Alerts';
      case 'patrolJurisdiction': return 'Patrol Jurisdiction';
      case 'photo': return 'Attachments';
      case 'details': return this.topLevelCategory === 'events' ? 'Caption' : 'Name & caption';
      case 'privacy': return 'Visibility';
    }
  }

  canAdvanceFromStep(step: WizardStep): boolean {
    switch (step) {
      case 'topLevel': return !!this.topLevelCategory;
      case 'eventsSubtype': return !!this.eventsSubtype;
      case 'fishSpecies': return !!this.selectedFishType;
      case 'placesSubtype': return this.isPlaceTypeSelected();
      case 'alertsSubtype': return this.isAlertTypeSelected();
      case 'patrolJurisdiction': return !!this.patrolJurisdiction;
      default: return true;
    }
  }

  // ── Mandatory-step model (progress bar + submit gating) ───────────────────
  //
  // The progress bar counts only the *mandatory* steps for the current path:
  // the type-definition chain plus Visibility. Optional steps (Name, Caption,
  // Attachments) are excluded, so the count varies by path.

  /** Ordered mandatory steps for the path selected so far. */
  mandatorySteps(): WizardStep[] {
    const steps: WizardStep[] = [];
    if (this.isEdit) steps.push('changeLocation');
    steps.push('topLevel');
    switch (this.topLevelCategory) {
      case 'events':
        steps.push('eventsSubtype');
        if (this.eventsSubtype === 'fish_caught') {
          steps.push('fishFamily');
          // Named family needs a species; "Others" skips the species step.
          if (this.fishFamily && this.fishFamily !== 'other') steps.push('fishSpecies');
        }
        break;
      case 'places':
        steps.push('placesSubtype');
        break;
      case 'alerts':
        steps.push('alertsSubtype');
        if (this.selectedPrimaryType?.id === 'patrol') steps.push('patrolJurisdiction');
        break;
    }
    // Alerts are always public → no Visibility step.
    if (this.topLevelCategory !== 'alerts') steps.push('privacy');
    return steps;
  }

  /** Whether a mandatory step has its required selection. */
  isStepComplete(step: WizardStep): boolean {
    switch (step) {
      case 'topLevel': return !!this.topLevelCategory;
      case 'eventsSubtype': return !!this.eventsSubtype;
      case 'fishFamily': return !!this.fishFamily;
      case 'fishSpecies': return !!this.selectedFishType;
      case 'placesSubtype': return this.isPlaceTypeSelected();
      case 'alertsSubtype': return this.isAlertTypeSelected();
      case 'patrolJurisdiction': return !!this.patrolJurisdiction;
      default: return true;
    }
  }

  /**
   * True once every mandatory step is satisfied. Drives the always-visible
   * top-right ✓ (blue and tappable when true, grayed out otherwise).
   */
  isReadyToSubmit(): boolean {
    // Edit: everything is prefilled and valid → the ✓ is always available.
    if (this.isEdit) return true;

    for (const step of this.mandatorySteps()) {
      if (!this.isStepComplete(step)) return false;
    }
    // Places & Events: light the ✓ as soon as the user reaches the first
    // optional field — for Fish Caught that's Quantity, otherwise Photo. They
    // can't submit straight from a subtype/family/species selection, but they
    // no longer have to walk all the way to Visibility. Alerts are exempt (no
    // visibility step), so they submit once the type chain is done.
    if (
      (this.topLevelCategory === 'places' || this.topLevelCategory === 'events') &&
      this.currentStep !== 'fishQuantity' &&
      this.currentStep !== 'photo' &&
      this.currentStep !== 'details' &&
      this.currentStep !== 'privacy'
    ) {
      return false;
    }
    return true;
  }

  get canSubmit(): boolean {
    if (this.isCreatingWaypoint || this.isDeleting) return false;
    // Edit seeds its coordinates from the waypoint, so it never waits on GPS —
    // and a waypoint stored without a position must still be editable. The one
    // hold is a custom-privacy waypoint whose audience is still loading.
    if (this.isEdit) return this.editSharedWithLoaded;
    return this.isReadyToSubmit() && this.canCreateWaypoint;
  }

  /**
   * Progress-bar fill (0–1) over the mandatory chain. 0-based so the first step
   * reads empty and the last mandatory step reads full; optional steps (past
   * the chain) clamp to full.
   */
  get progressFraction(): number {
    const mandatory = this.mandatorySteps();
    const total = mandatory.length;
    if (total <= 0) return 0;
    const idx = mandatory.indexOf(this.currentStep);
    const filled = idx >= 0 ? idx : total;
    const denom = total - 1;
    if (denom <= 0) return 1;
    return Math.min(1, Math.max(0, filled / denom));
  }

  /**
   * True for steps with an explicit "Skip" button. Selection steps auto-advance
   * on tap; the final step of each path (privacy, or details for alerts)
   * submits via the header ✓ with no Skip.
   */
  get stepHasBottomBar(): boolean {
    const step = this.currentStep;
    // Edit: every page is prefilled, so give every non-terminal step a Skip
    // button to move forward without changing the seeded value.
    if (this.isEdit) return this.nextStepFrom(step) !== step;
    switch (step) {
      case 'fishQuantity':
      case 'photo':
        return true;
      case 'details':
        // Alerts end at details (no visibility) → submit via ✓, no Skip.
        return this.topLevelCategory !== 'alerts';
      default:
        return false;
    }
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  submit() {
    if (!this.canSubmit) return;
    if (this.visibility === 'custom' && !this.hasShareAudience) {
      this.showError('Please select a share audience.');
      return;
    }
    if (this.isEdit) {
      this.submitEdit();
      return;
    }
    this.create();
  }

  private get hasShareAudience(): boolean {
    return this.dockmates.some((m) => m.selected) || this.fleets.some((f) => f.selected);
  }

  cancel() {
    this.close();
  }

  /** Resolves the backend `type` string + display name for the current picks. */
  private resolveTypeAndName(): { type: string; name: string } {
    const selectedEvent = this.selectedEventType;
    const selectedPlace = this.placeTypes.find((t) => t.selected);
    const selectedAlert = this.alertTypes.find((t) => t.selected);

    if (selectedAlert) return { type: selectedAlert.id, name: selectedAlert.name };
    if (selectedPlace) return { type: selectedPlace.id, name: selectedPlace.name };
    if (selectedEvent) {
      return {
        type: selectedEvent.payloadType || selectedEvent.id,
        name: selectedEvent.name,
      };
    }
    return { type: 'fish_mark', name: 'Fish Mark' };
  }

  /**
   * Backend `details` for the current selections: fish_caught
   * (category/subtype/quantity), spot/inlet (show_icon), patrol (jurisdiction).
   * Returns null when no extra details apply.
   */
  private buildDetailsPayload(typeToSend: string): Record<string, any> | null {
    if (typeToSend === this.fishCaughtTypeId) {
      if (this.isFishCaughtOther) {
        // "Others": generic fish caught with no named family/species.
        return { fish_subtype: 'other', fish_quantity: this.fishQuantity };
      }
      const subtype = this.selectedFishType?.id;
      if (subtype) {
        return {
          fish_category: this.selectedEventType?.categoryKey || 'tuna',
          fish_subtype: subtype,
          fish_quantity: this.fishQuantity,
        };
      }
      return { fish_subtype: 'other', fish_quantity: this.fishQuantity };
    }
    if (typeToSend === 'canyon' || typeToSend === 'inlet') {
      // show_icon is set explicitly by the "Appear as icon" toggle: on → the
      // pin shows its type icon; off → it shows its name label.
      return { show_icon: this.showIcon };
    }
    if (typeToSend === 'patrol' && this.patrolJurisdiction) {
      return { patrol_jurisdiction: this.patrolJurisdiction };
    }
    return null;
  }

  /**
   * Detail keys this wizard owns. On edit they're all dropped from the stored
   * details and re-applied from the current selections (so a type change clears
   * stale keys); any other keys the backend stored survive untouched.
   */
  /** Types the app treats as public alerts — never attached to a trip. */
  private static readonly alertTypeKeys = ['hazard', 'diver', 'patrol', 'fog'];

  private static readonly managedDetailKeys = [
    'fish_category',
    'fish_subtype',
    'fish_quantity',
    'show_icon',
    'patrol_jurisdiction',
  ];

  private buildEditDetailsPayload(typeToSend: string): Record<string, any> | null {
    const fresh = this.buildDetailsPayload(typeToSend) || {};
    const existing = this.editWaypoint?.details;
    if (!existing || Object.keys(existing).length === 0) {
      return Object.keys(fresh).length === 0 ? null : { ...fresh };
    }
    const merged: Record<string, any> = { ...existing };
    for (const key of WaypointComponent.managedDetailKeys) delete merged[key];
    Object.assign(merged, fresh);
    return Object.keys(merged).length === 0 ? null : merged;
  }

  /** Places and Alerts allow a custom title; otherwise use the chip label. */
  private buildNameToSend(fallbackName: string): string {
    const custom = this.placeName.trim();
    if (this.usesCustomTitle && custom) return custom;
    if (this.isFishCaughtSelected()) return this.selectedWaypointTitle;
    return fallbackName;
  }

  create() {
    if (!this.canCreateWaypoint) return;
    this.isCreatingWaypoint = true;

    const selectedPlaces = this.placeTypes.filter((t) => t.selected).map((t) => t.id);
    const selectedAlerts = this.alertTypes.filter((t) => t.selected).map((t) => t.id);
    const { type: finalType, name: typeName } = this.resolveTypeAndName();
    const finalName = this.buildNameToSend(typeName);

    const body: any = {
      name: finalName,
      type: finalType,
      places: selectedPlaces,
      alerts: selectedAlerts,
      description: this.description,
      privacy: selectedAlerts.length > 0 ? 'public' : this.visibility,

      data_bridge: this.dataBridgeEnabled,
      observation_summary: this.conditions ? {
        air_temperature: this.conditions.air_temperature,
        air_pressure: this.conditions.air_pressure,
        precipitation: this.conditions.precipitation,
        wind_speed: this.conditions.wind_speed,
        wave_height: this.conditions.wave_height,
        wave_period: this.conditions.wave_period,
        temp: this.conditions.temp,
        depth: this.conditions.depth,
        cog: this.conditions.cog,
        sog: this.conditions.sog,
        ts_utc: this.conditions.ts_utc,
        lat: this.coords?.lat,
        lon: this.coords?.lng,
        air_temperature_unit: this.conditions.air_temperature_unit || 'fahrenheit',
        water_temperature_unit: 'fahrenheit',
        water_depth_unit: 'feet',
        depth_unit: this.conditions.depth_unit || 'feet',
        wind_speed_unit: this.conditions.wind_speed_unit || 'mph'
      } : null
    };

    const details = this.buildDetailsPayload(finalType);
    if (details) body.details = details;

    // Attach the waypoint to whichever trip is live for this user: their own trip
    // first, else the ride-along they're following. Without the ride-along fallback
    // a rider's waypoint is created with no trip at all, so it never comes back from
    // GET /trip/{id}/ride-along-waypoints/ (which filters on trip_id) and the rider
    // never sees it under Added Waypoints. Matches the app.
    //
    // Alerts are the exception: the app sends an empty trip ref for
    // hazard/diver/patrol/fog. They're public warnings about a place, not part of
    // anyone's track — and attaching one makes HomePage's
    // handleWaypointAddedForTripTrack re-anchor the live trip line to the alert,
    // drawing a stray leg from the pin to the boat.
    const currentTripId = WaypointComponent.alertTypeKeys.includes(finalType)
      ? ''
      : this.tripService.getCurrentTripId() ||
        this.rideAlongService.getActiveRideAlong()?.tripId ||
        '';
    if (currentTripId) {
      body.trip_id = currentTripId;
      body.trip_ref = {
        identifier: currentTripId
      };
    }

    if (this.coords) {
      body.position = {
        latitude: this.coords.lat,
        longitude: this.coords.lng,
      };
    }

    const formData = this.toFormData(body);
    this.selectedMedia.forEach((media) => {
      formData.append('waypoint_image', media.file, media.file.name);
    });

    this.waypointService.createWaypoint(formData).subscribe({
      next: (res: any) => {
        this.clarityService.setAction('action_create_waypoint');
        this.clarityService.setLastWaypointCreated();
        this.submenuService.setNewWaypoint(res);
        this.close();

        // If the Create Waypoint guided tour is on the save step, advance it only
        // now — after the waypoint truly exists and the sheet has closed — so the
        // "Waypoint created" step never fires while the modal is still open.
        // (The save tick opts out of tap-to-advance via autoCompleteOnTap: false.)
        this.tourController.completeInteractiveStep('waypoint_tick');

        // Handle sharing if visibility is pier circle (custom)
        if (this.visibility === 'custom') {
          this.shareWithSelected(res._id);
        } else {
          this.showSuccess('Waypoint created successfully');
          this.isCreatingWaypoint = false;
        }
      },
      error: (err: any) => {
        console.error('Error creating waypoint:', err);
        this.showError('Failed to create waypoint');
        this.isCreatingWaypoint = false;
      },
    });
  }

  /**
   * EDIT submit: remove any deleted photos, PATCH the waypoint, then re-share
   * for custom privacy. Mirrors the app's `_submitEdit`.
   */
  private submitEdit() {
    const waypointId = this.editWaypoint?._id || this.editWaypoint?.id;
    if (!waypointId) {
      this.showError('Unable to update: missing waypoint id');
      return;
    }
    this.isCreatingWaypoint = true;

    const { type: finalType, name: typeName } = this.resolveTypeAndName();
    const body: any = {
      name: this.buildNameToSend(typeName),
      type: finalType,
      description: this.description,
      privacy: this.isAlertTypeSelected() ? 'public' : this.visibility,
    };

    const details = this.buildEditDetailsPayload(finalType);
    if (details) body.details = details;

    if (this.coords) {
      body.position = { latitude: this.coords.lat, longitude: this.coords.lng };
    }
    // Moved pin → drop the stored telemetry so stale readings don't stick to
    // the new coordinates.
    if (this.isPositionEdited) body.observation_summary = {};

    const patch = () => {
      const formData = this.toFormData(body);
      this.selectedMedia.forEach((media) => {
        formData.append('waypoint_image', media.file, media.file.name);
      });

      this.waypointService.UpdateWaypoint(waypointId, formData).subscribe({
        next: (res: any) => {
          const serverWp = (res && (res.data || res)) || {};
          const updated = {
            ...this.editWaypoint,
            ...body,
            ...serverWp,
            details: {
              ...(this.editWaypoint?.details || {}),
              ...(body.details || {}),
              ...(serverWp?.details || {}),
            },
          };
          this.submenuService.setWaypointUpdated(updated);
          this.submenuService.closeWaypointPopup(waypointId);

          if (this.visibility === 'custom') {
            this.shareWithSelected(waypointId, 'Waypoint updated successfully');
            this.close();
          } else {
            this.showSuccess('Waypoint updated successfully');
            this.isCreatingWaypoint = false;
            this.close();
          }
        },
        error: (err: any) => {
          console.error('Error updating waypoint:', err);
          this.showError('Failed to update waypoint');
          this.isCreatingWaypoint = false;
        },
      });
    };

    if (this.removedImageUrls.length > 0) {
      forkJoin(
        this.removedImageUrls.map((url) => this.waypointService.removeWaypointPhoto(waypointId, url))
      ).subscribe({
        next: () => { this.removedImageUrls = []; patch(); },
        error: (err) => { console.error('Error removing photos:', err); patch(); },
      });
      return;
    }
    patch();
  }

  /** Serializes a plain body object into multipart FormData. */
  private toFormData(body: Record<string, any>): FormData {
    const formData = new FormData();
    Object.keys(body).forEach((key) => {
      const val = body[key];
      if (val === null || val === undefined) return;
      formData.append(key, typeof val === 'object' ? JSON.stringify(val) : String(val));
    });
    return formData;
  }

  // ── Edit mode: seeding + delete ───────────────────────────────────────────

  /**
   * Seeds every wizard field from [wp] and parks the flow on the
   * change-location step. Mirrors the app's `_seedFromEditWaypoint`.
   */
  private seedFromEditWaypoint(wp: any) {
    this.editWaypoint = { ...wp };
    this.currentStep = 'changeLocation';
    this.history = [];
    // Resolve the restrictive-fleet seal before the privacy step is reachable.
    this.resolveRestrictedFleetRef(wp);

    const details = wp.details || {};
    const type = resolveWaypointType(wp);

    switch (type) {
      case 'fish_caught': {
        this.topLevelCategory = 'events';
        this.eventsSubtype = 'fish_caught';
        const subtype = String(details.fish_subtype || '').trim().replace(/-/g, '_');
        const catKey = String(details.fish_category || '').trim();
        const inferred = ['tuna', 'billfish', 'pelagic', 'inshore'].includes(catKey)
          ? catKey
          : this.inferFamilyFromSubtype(subtype);
        // A catch whose family can be inferred (explicit fish_category, or a
        // family-specific subtype — including that family's own "Other", stored
        // as `general_<family>`) seeds back into that NAMED family so the
        // species step highlights the right row. Only a truly family-less catch
        // (legacy 'other' / empty subtype with no category) becomes "Others".
        const isNamed = !!inferred && !!subtype && subtype !== 'other';
        if (isNamed) {
          this.fishFamily = inferred!;
          const family = this.eventTypes.find((t) => t.id === inferred);
          if (family) this.selectChip(family, 'eventTypes');
          const species = this.fishTypes.find((t) => t.id === subtype);
          if (species) this.selectChip(species, 'fishTypes');
        } else {
          this.fishFamily = 'other';
          const other = this.eventTypes.find((t) => t.id === this.fishCaughtOtherId);
          if (other) this.selectChip(other, 'eventTypes');
          this.fishTypes.forEach((t) => t.selected = false);
        }
        const rawQty = Number(details.fish_quantity);
        this.fishQuantity = Number.isFinite(rawQty) ? this.normalizeFishQuantity(rawQty) : 1;
        break;
      }
      case 'fish_mark':
      case 'bait_mark':
      case 'temp_break': {
        this.topLevelCategory = 'events';
        this.eventsSubtype = type;
        const entry = this.eventTypes.find((t) => t.id === type);
        if (entry) this.selectChip(entry, 'eventTypes');
        break;
      }
      case 'canyon':
      case 'wreck':
      case 'inlet':
      case 'anchorage':
      case 'home': {
        this.topLevelCategory = 'places';
        const entry = this.placeTypes.find((t) => t.id === type);
        if (entry) this.selectChip(entry, 'placeTypes');
        if (type === 'canyon' || type === 'inlet') this.showIcon = details.show_icon !== false;
        break;
      }
      case 'hazard':
      case 'diver':
      case 'patrol':
      case 'fog': {
        this.topLevelCategory = 'alerts';
        const entry = this.alertTypes.find((t) => t.id === type);
        if (entry) this.selectChip(entry, 'alertTypes');
        if (type === 'patrol') {
          const jur = String(details.patrol_jurisdiction || '').trim();
          if (jur) this.patrolJurisdiction = jur;
        }
        break;
      }
      default: {
        this.topLevelCategory = 'events';
        this.eventsSubtype = 'fish_mark';
        const entry = this.eventTypes.find((t) => t.id === 'fish_mark');
        if (entry) this.selectChip(entry, 'eventTypes');
      }
    }

    // Title (places/alerts) — seed the real custom name but suppress a bare
    // type-label default (an unnamed pin stores its name as the type label).
    // For spot/inlet the stored show_icon disambiguates: a genuinely named pin
    // has show_icon=false even when the name equals the label.
    const name = String(wp.name || '').trim();
    if (this.usesCustomTitle && name) {
      const label = this.selectedPrimaryType?.name || '';
      const isSpotOrInlet = this.isSpotOrInletSelected();
      const storedShowIcon = details.show_icon === true;
      const isBareLabelDefault = name === label && !(isSpotOrInlet && !storedShowIcon);
      if (!isBareLabelDefault) this.placeName = name;
    }

    this.description = wp.description || '';

    const lat = wp.position?.latitude;
    const lng = wp.position?.longitude;
    if (lat != null && lng != null) {
      this.coords = { lat, lng };
      this.originalCoords = { lat, lng };
    }

    this.existingImageUrls = this.extractImageUrls(wp);

    if (this.isAlertTypeSelected()) {
      this.visibility = 'public';
    } else {
      const privacy = String(wp.privacy || 'public').trim().toLowerCase();
      this.visibility = privacy === 'private' ? 'private' : privacy === 'custom' ? 'custom' : 'public';
      if (this.visibility === 'custom') {
        // Load the rosters with this waypoint's existing audience pre-selected,
        // holding the ✓ until they land.
        this.editSharedWithLoaded = false;
        this.fetchFleetsAndFriendsForWaypoint(wp._id || wp.id);
      }
    }

    this.cdr.detectChanges();
  }

  private inferFamilyFromSubtype(subtype: string): string | null {
    if (!subtype) return null;
    const families: Record<string, string[]> = {
      tuna: ['yellowfin', 'bluefin', 'bigeye', 'albacore', 'general_tuna'],
      billfish: ['bluemarlin', 'whitemarlin', 'sailfish', 'swordfish', 'general_billfish'],
      pelagic: ['mahi', 'wahoo', 'general_pelagic'],
      inshore: ['stripedbass', 'fluke', 'seabass', 'tautog', 'general_inshore'],
    };
    for (const [family, keys] of Object.entries(families)) {
      if (keys.includes(subtype)) return family;
    }
    return null;
  }

  private extractImageUrls(wp: any): string[] {
    const raw = wp?.image_urls || wp?.images || [];
    if (!Array.isArray(raw)) return [];
    return raw
      .map((item: any) => (typeof item === 'string' ? item : item?.image_url))
      .filter((url: any): url is string => typeof url === 'string' && url.length > 0);
  }

  requestDelete() {
    if (this.isCreatingWaypoint) return;
    this.showDeleteConfirm = true;
  }

  cancelDelete() {
    this.showDeleteConfirm = false;
  }

  confirmDelete() {
    const waypointId = this.editWaypoint?._id || this.editWaypoint?.id;
    if (!waypointId || this.isDeleting) return;
    this.isDeleting = true;
    this.waypointService.deleteWaypoint(waypointId).subscribe({
      next: () => {
        this.submenuService.setWaypointDeleted(waypointId);
        this.showSuccess('Waypoint deleted successfully');
        this.isDeleting = false;
        this.showDeleteConfirm = false;
        this.close();
      },
      error: (err) => {
        console.error('Error deleting waypoint:', err);
        this.showError('Failed to delete waypoint');
        this.isDeleting = false;
        this.showDeleteConfirm = false;
      },
    });
  }

  shareWithSelected(waypointId: string, successMessage = 'Waypoint created successfully') {
    const friend_ids = this.dockmates
      .filter(m => m.selected)
      .map(m => m.owner_ref?.identifier || m._id); // Prefer owner_ref.identifier as per specs

    const fleet_ids = this.fleets
      .filter(f => f.selected)
      .map(f => f._id);

    if (friend_ids.length === 0 && fleet_ids.length === 0) {
      this.showSuccess(successMessage);
      this.isCreatingWaypoint = false;
      return;
    }

    const payload = {
      friend_ids,
      fleet_ids,
      message: this.description
    };

    this.waypointService.shareWaypoint(waypointId, payload).subscribe({
      next: (res) => {
        this.showSuccess(res.message || 'Waypoint shared successfully');
        this.isCreatingWaypoint = false;
      },
      error: (err) => {
        console.error('Error sharing waypoint:', err);
        this.showError('Waypoint saved but sharing failed');
        this.isCreatingWaypoint = false;
      }
    });
  }


  showSuccess(message: string) {
    this.toast.success(message);
  }

  showError(message: string) {
    this.toast.error(message);
  }

  formatCoordinate(val: number, type: 'lat' | 'lng'): string {
    if (val === undefined || val === null || isNaN(val)) return '--';
    const absVal = Math.abs(val);
    const degrees = Math.floor(absVal);
    const minutesFull = (absVal - degrees) * 60;
    const minutes = Math.floor(minutesFull);
    const seconds = Math.round((minutesFull - minutes) * 60);
    const direction = type === 'lat' ? (val >= 0 ? 'N' : 'S') : (val >= 0 ? 'E' : 'W');
    return `${degrees}° ${minutes}' ${seconds}" ${direction}`;
  }

  getFullCoordinates(): string {
    if (!this.coords) {
      return this.isResolvingCoordinates ? 'Fetching coordinates...' : 'Coordinates unavailable';
    }
    return `${this.formatCoordinate(this.coords.lat, 'lat')} ${this.formatCoordinate(this.coords.lng, 'lng')}`;
  }

  getStaticMapUrl(): string {
    if (!this.coords || isNaN(this.coords.lat) || isNaN(this.coords.lng)) return '';
    const zoom = 14;
    const width = 600;
    const height = 400;
    const mapId = '019adf7b-1688-7ff4-ae90-c50ef00338f9';
    return `https://api.maptiler.com/maps/${mapId}/static/${this.coords.lng},${this.coords.lat},${zoom}/${width}x${height}.png?key=${environment.maptilerKey}`;
  }

  selectPrivacy(val: 'private' | 'custom' | 'public') {
    if (this.isAlertTypeSelected() && val !== 'public') return;
    if (val === 'public' && this.isEveryoneOptionDisabled) return;
    this.visibility = val;
    this.repaint();
  }

  // ── Restricted-fleet lock ─────────────────────────────────────────────────
  //
  // A waypoint that belongs to a restrictive fleet — either directly
  // (`restricted_fleet_ref`) or through the trip it was logged on — is sealed to
  // that fleet. The app models this with `_hasRestrictedFleetRef`, and it is a
  // property of the WAYPOINT, not of the current checkbox state. That distinction
  // is the whole bug: the web only looked at "is a restrictive fleet currently
  // selected", so switching privacy to Private cleared the selection, dropped the
  // lock, and the waypoint could then be shared to anyone.
  //
  // While the ref is present the app loads ONLY that one fleet into the picker
  // and no dockmates at all (`_loadDockmatesAndFleets`), which is what makes
  // "share to a restrictive fleet and another fleet simultaneously" impossible
  // rather than merely disallowed.

  /** `{identifier, name, type}` of the fleet this waypoint is sealed to, or null. */
  restrictedFleetRef: { identifier: string; name: string; type?: string } | null = null;

  get hasRestrictedFleetRef(): boolean {
    return !!this.restrictedFleetRef?.identifier?.trim();
  }

  /** Reads the app's `TripRestrictedFleetRef` shape off any waypoint/trip payload. */
  private parseRestrictedFleetRef(source: any): { identifier: string; name: string; type?: string } | null {
    const raw = source?.restricted_fleet_ref;
    if (!raw || typeof raw !== 'object') return null;
    const identifier = String(raw.identifier || raw.reference_id || raw.ref_id || raw._id || '').trim();
    if (!identifier) return null;
    return { identifier, name: String(raw.name || '').trim(), type: raw.type };
  }

  /**
   * Resolve the lock for the waypoint being edited: its own ref first, then the
   * ref of the trip it belongs to — the app's `_ensureTripSharingContextLoaded`.
   */
  private resolveRestrictedFleetRef(wp: any): void {
    this.restrictedFleetRef = this.parseRestrictedFleetRef(wp);
    if (this.restrictedFleetRef) return;

    const tripId = String(
      wp?.trip_ref?.reference_id || wp?.trip_ref?.ref_id || wp?.trip_ref?.identifier ||
      wp?.trip_ref?._id || wp?.trip_id || ''
    ).trim();
    if (!tripId) return;

    this.tripService.getTripById(tripId).pipe(catchError(() => of(null))).subscribe((res: any) => {
      const trip = res?.data || res;
      const ref = this.parseRestrictedFleetRef(trip);
      if (!ref) return;
      this.restrictedFleetRef = ref;
      // The rosters may already be loaded against the unlocked assumption.
      if (this.visibility === 'custom') this.fetchFleetsAndFriends();
      if (this.visibility === 'public') this.visibility = 'custom';
      this.cdr.detectChanges();
    });
  }

  /**
   * `is_restrictive` as the app parses it (`FleetModel.fromJson`): the backend
   * has returned it as both a bool and a string, and `!!'false'` is `true`.
   */
  private parseIsRestrictive(raw: any): boolean {
    if (raw === true) return true;
    return String(raw ?? '').trim().toLowerCase() === 'true';
  }

  /**
   * The viewer's role in a fleet, resolved through every shape the API uses.
   * `user_membership` is an OBJECT (`{role: …}`) from `CommunitySerializer`, which
   * the web never unwrapped — so the role read as undefined, `isRestrictiveAsMember`
   * could not tell a member from an admin, and no row ever locked.
   */
  private parseFleetRole(f: any): string {
    const membership = f?.user_membership;
    if (membership && typeof membership === 'object' && membership.role) return String(membership.role);
    if (typeof membership === 'string' && membership) return membership;
    return String(f?.my_role ?? f?.user_role ?? f?.role ?? '');
  }

  // ── Restrictive-sharing rules (edit + create) ─────────────────────────────
  //
  // A restrictive fleet's content must not leak outside that fleet, so a
  // restrictive fleet can only ever be the SOLE recipient, and "The Water
  // (Everyone)" is unavailable while one is selected.
  //
  // The Send sheet and the legacy edit form both enforced this; this wizard —
  // which is now what the Edit button opens — did not, so the rule could be
  // bypassed simply by editing a waypoint instead of sending it. Same shared
  // helpers the other two use, mirroring the app's `restrictive_sharing.dart`
  // and `waypoint_sheet.dart`'s `_buildPrivacyOption`.

  /** Fleet list in the shape the shared restrictive helpers expect. */
  private get privacyFleetRefs(): RestrictiveFleet[] {
    return this.fleets.map((f: any) => ({
      id: String(f.id ?? f._id ?? f.community_id ?? ''),
      isRestrictive: !!f.restrictsSharing,
    }));
  }

  /** Current selection in the app's keyspace: dockmates 0..N, fleets 100000+N. */
  private get privacySelectedKeys(): Set<number> {
    const keys = new Set<number>();
    this.dockmates.forEach((m: any, i: number) => { if (m.selected) keys.add(i); });
    this.fleets.forEach((f: any, i: number) => { if (f.selected) keys.add(FLEET_RECIPIENT_OFFSET + i); });
    return keys;
  }

  /**
   * True once this waypoint is ALREADY shared to a restrictive fleet.
   *
   * The rules below otherwise reason purely about what is currently ticked, and
   * that is not enough on the edit path: choosing "Private" clears the ticks, so
   * `hasRestrictiveFleetSelection` went false and every other fleet unlocked —
   * "once I make a waypoint private, even though it was shared to a restrictive
   * fleet, I can suddenly share it to any fleet". The seal has to come from what
   * the waypoint IS, not from what is momentarily checked. Same guard the legacy
   * edit form uses (`waypoints.component.ts#privacyHasRestrictiveShare`).
   */
  get privacyHasRestrictiveShare(): boolean {
    return this.fleets.some((f: any) => f.alreadyShared && f.restrictsSharing);
  }

  isPrivacyFleetLocked(fleet: any): boolean {
    // The fleet the waypoint is already sealed to stays interactive — locking it
    // too would leave the user staring at a list where nothing can be touched.
    if (fleet?.alreadyShared) return false;
    if (this.privacyHasRestrictiveShare) return true;
    const index = this.fleets.indexOf(fleet);
    if (index < 0) return false;
    return isRestrictiveRecipientDisabled(
      FLEET_RECIPIENT_OFFSET + index,
      !!fleet.selected,
      this.privacySelectedKeys,
      this.privacyFleetRefs,
    );
  }

  isPrivacyDockmateLocked(mate: any): boolean {
    if (mate?.alreadyShared) return false;
    if (this.privacyHasRestrictiveShare) return true;
    const index = this.dockmates.indexOf(mate);
    if (index < 0) return false;
    return isRestrictiveRecipientDisabled(
      index,
      !!mate.selected,
      this.privacySelectedKeys,
      this.privacyFleetRefs,
    );
  }

  togglePrivacyFleet(fleet: any): void {
    const index = this.fleets.indexOf(fleet);
    if (index < 0 || this.isPrivacyFleetLocked(fleet)) return;
    // A waypoint sealed to a restrictive fleet cannot be unshared from it here.
    if (this.hasRestrictedFleetRef) return;
    this.applyPrivacySelection(
      toggledRestrictiveRecipientSelection(
        this.privacySelectedKeys,
        FLEET_RECIPIENT_OFFSET + index,
        this.privacyFleetRefs,
      ),
    );
    this.repaint();
  }

  togglePrivacyDockmate(mate: any): void {
    const index = this.dockmates.indexOf(mate);
    if (index < 0 || this.isPrivacyDockmateLocked(mate)) return;
    this.applyPrivacySelection(
      toggledRestrictiveRecipientSelection(
        this.privacySelectedKeys,
        index,
        this.privacyFleetRefs,
      ),
    );
    this.repaint();
  }

  /** Writes a key-set produced by the shared helpers back onto the checkboxes. */
  private applyPrivacySelection(keys: Set<number>): void {
    this.dockmates.forEach((m: any, i: number) => { m.selected = keys.has(i); });
    this.fleets.forEach((f: any, i: number) => { f.selected = keys.has(FLEET_RECIPIENT_OFFSET + i); });
  }

  /**
   * "The Water (Everyone)" is unavailable while a restrictive fleet is selected —
   * making it public would defeat the restriction. The app's
   * `disabledByRestrictiveSharing` on privacy option index 0.
   */
  get isEveryoneOptionDisabled(): boolean {
    // `hasRestrictedFleetRef` is the half that was missing: a waypoint sealed to
    // a restrictive fleet must stay unpublishable even when nothing is currently
    // ticked, which is the state you land in right after choosing Private.
    if (this.hasRestrictedFleetRef) return true;
    // Same reasoning as `privacyHasRestrictiveShare`: a waypoint already shared
    // into a restrictive fleet must stay unpublishable after Private clears the
    // ticks, not just while the fleet happens to be checked.
    if (this.privacyHasRestrictiveShare) return true;
    return this.fleets.some((f: any) => f.selected && f.restrictsSharing);
  }

  /**
   * @param fromExistingShares  True only when the id lists came from the
   *   waypoint's own `shared_with` (the edit path). Ids can also arrive from the
   *   user's *default sharing* preference on the create path, and a preference
   *   must not seal a brand-new waypoint to a fleet — so only the edit path
   *   stamps `alreadyShared`, which is what `privacyHasRestrictiveShare` reads.
   */
  fetchFleetsAndFriends(
    defaultFleetIds: string[] = [],
    defaultFriendIds: string[] = [],
    fromExistingShares: boolean = false,
  ) {
    // Sealed to a restrictive fleet → that fleet is the ONLY recipient the picker
    // ever offers, and there are no dockmate rows at all. Mirrors the app's
    // `_loadDockmatesAndFleets` short-circuit, including its synthetic fallback
    // when the fleet's details can't be fetched.
    if (this.hasRestrictedFleetRef) {
      const ref = this.restrictedFleetRef!;
      this.dockmates = [];
      this.socialService.getCommunityById(ref.identifier)
        .pipe(catchError(() => of(null)))
        .subscribe((res: any) => {
          const fleet = res?.data || res;
          const resolved = fleet && (fleet._id || fleet.id)
            ? { ...fleet }
            : { _id: ref.identifier, name: ref.name || 'Restricted Fleet', members_count: 0 };
          this.fleets = [{
            ...resolved,
            selected: true,
            isRestrictive: true,
            restrictsSharing: true,
          }];
          if (this.visibility === 'public') this.visibility = 'custom';
          this.editSharedWithLoaded = true;
          this.cdr.detectChanges();
        });
      return;
    }

    forkJoin([
      this.waypointService.getFleets(1, 100).pipe(catchError(() => of({ results: [] }))),
      this.socialService.myDockmates(1, 100).pipe(catchError(() => of({ results: [] }))),
    ]).subscribe(([fleetsRes, matesRes]: any[]) => {
      this.fleets = (fleetsRes.results || []).map((f: any) => {
        // A fleet can be addressed by any of these depending on the endpoint
        // that produced the id list (community_id comes back from shared_with).
        const selected = [f.id, f._id, f.community_id].some(
          (id: any) => !!id && defaultFleetIds.includes(id)
        );
        // `restrictsSharing` is the ROLE-AWARE flag the sharing rules run on: a
        // fleet's owner/admin is not bound by its restriction, only its members
        // are (`isRestrictiveAsMember`). The raw flag stays for the badge.
        const isRestrictive = this.parseIsRestrictive(f.is_restrictive ?? f.isRestrictive);
        return {
          ...f,
          selected,
          // Only meaningful on the edit path — see the `fromExistingShares` note
          // on this method. This is the flag that survives the user switching the
          // waypoint to Private, which is exactly when the tick-based rules stop
          // protecting a restrictive fleet.
          alreadyShared: fromExistingShares && selected,
          isRestrictive,
          restrictsSharing: isRestrictiveAsMember({
            id: String(f.id ?? f._id ?? f.community_id ?? ''),
            isRestrictive,
            myRole: this.parseFleetRole(f),
          }),
        };
      });

      // API returns CommunityMember objects with nested user
      const tokenUserId = this.authService.getUserIdFromToken();
      const objUserId = this.authService.getUserObjectId();
      const results = (matesRes.results || []).filter((m: any) => {
        const ids = [m._id, m.user?._id, m.user?.user_id, m.owner_ref?.identifier, m.member_ref?.identifier];
        return !ids.includes(tokenUserId) && !ids.includes(objUserId);
      });
      this.dockmates = results.map((m: any) => {
        const id = m.user?.user_id || m.user?._id || m.user?.id || m._id;
        return {
          ...m,
          name: m.user?.name || m.display_name || m.name,
          image_url: m.user?.image_url || m.image_url,
          selected: defaultFriendIds.includes(id),
          alreadyShared: fromExistingShares && defaultFriendIds.includes(id),
        };
      });

      // Both rosters are in. A pre-selected restrictive fleet must be the sole
      // recipient, so normalise once here — an audience the backend already
      // holds (or a default-sharing preference) must not arrive in a state the
      // UI would refuse to let you build by hand.
      this.applyPrivacySelection(
        normalizeRestrictiveRecipientSelection(this.privacySelectedKeys, this.privacyFleetRefs),
      );
      if (this.isEveryoneOptionDisabled && this.visibility === 'public') {
        this.visibility = 'custom';
      }

      // Both rosters are in — release the edit ✓.
      this.editSharedWithLoaded = true;
      this.cdr.detectChanges();
    });
  }

  /** Edit + custom privacy: pre-select the audience this waypoint is shared with. */
  private fetchFleetsAndFriendsForWaypoint(waypointId: string) {
    if (!waypointId) {
      this.fetchFleetsAndFriends();
      return;
    }
    this.waypointService.getWaypointSharedWith(waypointId).subscribe({
      next: (res: any) => {
        // Shape: { shared_with: { friends: [{ user_id }], fleets: [{ community_id }] } }
        const sw = res?.shared_with || res || {};
        const fleetIds = (sw.fleets || []).map((f: any) => f?.community_id).filter(Boolean);
        const friendIds = (sw.friends || []).map((f: any) => f?.user_id).filter(Boolean);
        // `true` — these ids are the waypoint's real audience, so they stamp
        // `alreadyShared` and seal a restrictive fleet for the rest of the edit.
        this.fetchFleetsAndFriends(fleetIds, friendIds, true);
      },
      error: () => this.fetchFleetsAndFriends(),
    });
  }

  get filteredFleets() {
    if (!this.visibilitySearchQuery) return this.fleets;
    return this.fleets.filter(f => f.name?.toLowerCase().includes(this.visibilitySearchQuery.toLowerCase()));
  }

  get filteredDockmates() {
    if (!this.visibilitySearchQuery) return this.dockmates;
    return this.dockmates.filter(m => m.name?.toLowerCase().includes(this.visibilitySearchQuery.toLowerCase()));
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  }

  getPrivacyDisplay(privacy: string): string {
    if (privacy === 'public') return 'Everyone';
    if (privacy === 'private') return 'Private';
    if (privacy === 'custom') return 'Pier Circle';
    return 'Everyone';
  }
}
